import { CHECKLIST_ITEMS } from '@/db/checklist'
import type { InspectionPriority, InspectionResult, InspectionResultStatus } from '@/db/types'

export interface InspectionResultSummary {
  good: number
  attention: number
  critical: number
  unanswered: number
  answered: number
  total: 38
  progressPercent: number
  priority: InspectionPriority
  provisional: boolean
}

export type InspectionSystemAggregate = 'good' | 'attention' | 'critical' | 'pending'

export interface InspectionSystemSummary {
  systemId: string
  systemLabel: string
  systemOrder: number
  total: number
  answered: number
  unanswered: number
  good: number
  attention: number
  critical: number
  aggregate: InspectionSystemAggregate
}

export function calculateInspectionPriority(
  statuses: readonly InspectionResultStatus[],
): InspectionPriority {
  if (statuses.includes('critical')) return 'urgent_stop_immediately'
  if (statuses.includes('attention')) return 'schedule_within_15_days'
  if (statuses.includes('good')) return 'monitor'
  return 'pending'
}

function validateResults(
  results: readonly Pick<InspectionResult, 'checklistItemId' | 'status'>[],
): Map<string, InspectionResultStatus> {
  const expectedIds = new Set(CHECKLIST_ITEMS.map((item) => item.id))
  const statusesByItem = new Map<string, InspectionResultStatus>()

  for (const result of results) {
    if (!expectedIds.has(result.checklistItemId)) {
      throw new Error(`Item de checklist desconhecido: ${result.checklistItemId}.`)
    }
    if (statusesByItem.has(result.checklistItemId)) {
      throw new Error(`Item de checklist duplicado: ${result.checklistItemId}.`)
    }
    statusesByItem.set(result.checklistItemId, result.status)
  }

  return statusesByItem
}

export function summarizeInspectionResults(
  results: readonly Pick<InspectionResult, 'checklistItemId' | 'status'>[],
): InspectionResultSummary {
  const statusesByItem = validateResults(results)
  const statuses = CHECKLIST_ITEMS.map(
    (item) => statusesByItem.get(item.id) ?? ('unanswered' as const),
  )
  const good = statuses.filter((status) => status === 'good').length
  const attention = statuses.filter((status) => status === 'attention').length
  const critical = statuses.filter((status) => status === 'critical').length
  const unanswered = statuses.filter((status) => status === 'unanswered').length
  const answered = good + attention + critical

  return {
    good,
    attention,
    critical,
    unanswered,
    answered,
    total: 38,
    progressPercent: (answered / CHECKLIST_ITEMS.length) * 100,
    priority: calculateInspectionPriority(statuses),
    provisional: unanswered > 0,
  }
}

export function summarizeInspectionSystems(
  results: readonly Pick<InspectionResult, 'checklistItemId' | 'status'>[],
): InspectionSystemSummary[] {
  const statusesByItem = validateResults(results)
  const systemIds = [...new Set(CHECKLIST_ITEMS.map((item) => item.systemId))]

  return systemIds.map((systemId) => {
    const items = CHECKLIST_ITEMS.filter((item) => item.systemId === systemId)
    const first = items[0]
    if (!first) throw new Error(`Sistema oficial desconhecido: ${systemId}.`)
    const statuses = items.map((item) => statusesByItem.get(item.id) ?? ('unanswered' as const))
    const good = statuses.filter((status) => status === 'good').length
    const attention = statuses.filter((status) => status === 'attention').length
    const critical = statuses.filter((status) => status === 'critical').length
    const unanswered = statuses.filter((status) => status === 'unanswered').length
    const aggregate: InspectionSystemAggregate =
      unanswered > 0 ? 'pending' : critical > 0 ? 'critical' : attention > 0 ? 'attention' : 'good'

    return {
      systemId,
      systemLabel: first.systemLabel,
      systemOrder: first.systemOrder,
      total: items.length,
      answered: items.length - unanswered,
      unanswered,
      good,
      attention,
      critical,
      aggregate,
    }
  })
}

export function findFirstPendingResult<T extends Pick<InspectionResult, 'itemOrder' | 'status'>>(
  results: readonly T[],
): T | undefined {
  return [...results]
    .sort((left, right) => left.itemOrder - right.itemOrder)
    .find((result) => result.status === 'unanswered')
}
